import { FC, useCallback, useContext, useMemo, useState } from 'react';
import { CgMathPlus, CgPen } from 'react-icons/cg';
import { MdMoreVert } from 'react-icons/md';
import { generatePath, Link } from 'react-router';
import {
  Button,
  HStack,
  IconButton,
  Menu,
  Portal,
  Stack,
  Text,
} from '@chakra-ui/react';
import { ColumnDef } from '@tanstack/react-table';

import { useOpenClose, UseOpenCloseReturn } from '~/core/hooks/useOpenClose';
import {
  EnvelopesSearchParams,
  useSendEnvelope,
  useSignatureEnvelopes,
} from '~/core/shared/api/signature';
import { DateTimeDisplayMode } from '~/core/shared/components/DateTimeDisplay/constants/DateTimeDisplayMode';
import { formatDateTime } from '~/core/shared/components/DateTimeDisplay/logic/formatDateTime';
import { Permissions } from '~/core/shared/generated/Permissions';
import { useRequiredParams, useTableParams } from '~/core/shared/hooks';
import { useCan } from '~/core/shared/hooks/useCan';
import { useEventHandler } from '~/core/shared/hooks/useEventHandler';
import { queryClient } from '~/core/shared/lib/reactQuery';
import { Envelope, Sorting } from '~/core/shared/types';
import { isDeniedHook } from '~/core/shared/utils/general';
import { useSubscribeToEnvelopeUpdated } from '~/core/shared/websocketApi';
import { ReactQueryTable } from '../../table/ReactQueryTable.component';
import {
  ApplicationEnvelopesCreateModal,
  ApplicationEnvelopesVoidModal,
} from '../envelopes';
import { ApplicationContext } from '../Layout.component';

type EnvelopesTabProps = {
  definition: string;
  editPath: string;
};

const getColumns = (
  can: (permission: string) => boolean,
  editPath: string,
  uuid: string,
  sendEnvelope: (envelope: Envelope) => void,
  openModal: (modal: any, envelope: Envelope) => void,
  voidModalApi: UseOpenCloseReturn,
): ColumnDef<Envelope>[] => [
  {
    header: 'Name',
    accessorKey: 'name',
    cell: ({ row }) => (
      <HStack>
        <Text asChild py={4}>
          <label htmlFor={`select-${row.original.id}`}>
            {row.original.name}
          </label>
        </Text>
      </HStack>
    ),
  },
  {
    header: 'Created At',
    accessorKey: 'created_at',
    cell: ({ row }) => (
      <Text>
        {formatDateTime(row.original.created_at, DateTimeDisplayMode.DateTime)}
      </Text>
    ),
  },
  {
    header: 'Created By',
    accessorKey: 'created_by',
    cell: ({ row }) => (
      <Text>
        {row.original.created_by ? row.original.created_by.name : 'System'}
      </Text>
    ),
  },
  {
    header: 'Status',
    accessorKey: 'latest_log',
    cell: ({ row }) => (
      <Text>{row.original.latest_log?.status ?? 'Draft'}</Text>
    ),
  },
  {
    header: 'Sent By',
    accessorKey: 'sent_by',
    cell: ({ row }) => {
      if (!row.original.sent_at) {
        return <Text />;
      }
      return <Text>{row.original.sent_by?.name || 'System'}</Text>;
    },
  },
  {
    header: 'Sent At',
    accessorKey: 'sent_at',
    cell: ({ row }) => (
      <Text>
        {row.original.sent_at
          ? formatDateTime(row.original.sent_at, DateTimeDisplayMode.DateTime)
          : ''}
      </Text>
    ),
  },
  {
    accessorKey: 'id',
    enableSorting: false,
    cell: ({ row }) => (
      <HStack gap={6} justifyContent="flex-end" w="full">
        {can(Permissions.EnvelopesUpdate) && (
          <>
            <Button
              asChild
              colorPalette="gray"
              fontWeight="normal"
              size="md"
              variant="ghost"
            >
              <Link
                to={generatePath(editPath, {
                  uuid,
                  envelope: row.original.id,
                })}
              >
                <CgPen />
                Edit
              </Link>
            </Button>
            <Menu.Root>
              <Menu.Trigger asChild>
                <IconButton colorPalette="gray" lineHeight={0} variant="ghost">
                  <MdMoreVert />
                </IconButton>
              </Menu.Trigger>
              <Portal>
                <Menu.Positioner>
                  <Menu.Content>
                    <Menu.Item
                      onClick={() => sendEnvelope(row.original)}
                      value="send"
                    >
                      Send
                    </Menu.Item>
                    {row.original.is_voidable && (
                      <Menu.Item
                        onClick={() => openModal(voidModalApi, row.original)}
                        value="void"
                      >
                        Void
                      </Menu.Item>
                    )}
                  </Menu.Content>
                </Menu.Positioner>
              </Portal>
            </Menu.Root>
          </>
        )}
      </HStack>
    ),
  },
];

const invalidateEnvelopesQuery = () => {
  queryClient.invalidateQueries({ queryKey: ['envelopes'] });
};

const defaultSorting: Sorting = { column: 'created_at', direction: 'desc' };

export const EnvelopesTab: FC<EnvelopesTabProps> = ({
  editPath = '/admin/applications/:uuid/envelope/:envelope',
}) => {
  const { sendEnvelope: sendEnvelopeRequest } = useSendEnvelope();
  const { uuid } = useRequiredParams(['uuid']);
  const { can } = useCan();
  const createModalApi = useOpenClose();
  const voidModalApi = useOpenClose();
  const [sending, setSending] = useState<string[]>([]);
  const [selectedEnvelope, setSelectedEnvelope] = useState<Envelope>();

  const { definition, hooks } = useContext(ApplicationContext);
  const disabled = isDeniedHook(hooks, 'envelopeStore');

  const { tableProps, searchParams } = useTableParams(defaultSorting, 25);

  const {
    envelopes,
    meta: envelopesMeta,
    ...envelopesQueryState
  } = useSignatureEnvelopes(uuid, searchParams as EnvelopesSearchParams, {
    enabled: !!definition,
  });

  useSubscribeToEnvelopeUpdated(uuid, () => {
    invalidateEnvelopesQuery();
  });

  const openModal = useCallback((modal: any, envelope: Envelope) => {
    setSelectedEnvelope(envelope);
    modal.onOpen();
  }, []);

  const sendEnvelope = useEventHandler(async (envelope: Envelope) => {
    if (!sending.includes(envelope.id)) {
      setSending([...sending, envelope.id]);
      try {
        await sendEnvelopeRequest({
          envelopeId: envelope.id,
          documentParams: { definition },
        });
        invalidateEnvelopesQuery();
      } catch {
        // handled in useSendEnvelope hook
      }
      setSending(sending.filter((sentId) => sentId !== envelope.id));
    }
  });

  const columns = useMemo(
    () =>
      getColumns(can, editPath, uuid, sendEnvelope, openModal, voidModalApi),
    [can, editPath, uuid, sendEnvelope, openModal, voidModalApi],
  );

  return (
    <>
      <ApplicationEnvelopesCreateModal
        editPath={editPath}
        modalApi={createModalApi}
      />
      {selectedEnvelope && (
        <ApplicationEnvelopesVoidModal
          envelope={selectedEnvelope}
          modalApi={voidModalApi}
        />
      )}

      <Stack gap={8}>
        {can(Permissions.EnvelopesCreate) && (
          <HStack justifyContent="flex-end">
            <Button
              colorPalette="gray"
              disabled={disabled}
              fontWeight="normal"
              onClick={createModalApi.onOpen}
              size="md"
              variant="outline"
            >
              <CgMathPlus />
              Start envelope
            </Button>
          </HStack>
        )}
        <ReactQueryTable<Envelope>
          columns={columns}
          data={envelopes}
          isLoading={envelopesQueryState.isFetching}
          meta={envelopesMeta}
          {...tableProps}
        />
      </Stack>
    </>
  );
};
